/**
 * Microphone Hook
 *
 * Single source of truth for microphone permission and device management.
 * Queries browser APIs directly on mount and keeps state fresh via event listeners.
 *
 * This hook replaces the previous atom-based architecture where browser state
 * was cached in Jotai atoms (including atomWithStorage for device selection).
 * The old approach could desync when:
 * - localStorage had a stale device ID from a previous session
 * - Permission state changed while the app was backgrounded
 * - Device enumeration failed silently
 *
 * Now, all state is derived fresh from browser APIs on every mount.
 * No localStorage persistence means no stale state.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAtomValue } from 'jotai';

import { createDebugLogger, useLatest } from '@videa/utils';

import {
  type AudioDevice,
  COMMUNICATIONS_LABEL_MARKER,
  type MicrophonePermissionRequestResult,
  type MicrophonePermissionState,
  MicrophonePermissionPromptBlockedError,
  MicrophonePermissionTimeoutError,
  PERMISSION_REQUEST_TIMEOUT_MS,
  buildAudioConstraints,
  deduplicateAudioDevices,
  findApprovedDevice,
  withTimeout,
} from '../contexts/microphone';
import {
  hasAudioActivityAtom,
  isRecordingSessionActiveAtom,
} from '../atoms/recording';

const debug = createDebugLogger('Microphone');

// ============================================================================
// Types
// ============================================================================

export interface UseMicrophoneReturn {
  // Permission state
  permissionState: MicrophonePermissionState;
  hasPermission: boolean;
  canRecord: boolean;

  // Devices
  devices: AudioDevice[];
  selectedDeviceId: string | null;
  selectedDevice: AudioDevice | null;
  hasValidSelection: boolean;

  // Actions
  requestPermission: () => Promise<MicrophonePermissionRequestResult>;
  refreshDevices: () => Promise<string | null>;
  selectDevice: (deviceId: string | null) => void;
  getAudioConstraints: () => MediaTrackConstraints;

  // Status
  isLoading: boolean;
  error: Error | null;
}

export interface UseMicrophoneOptions {
  /**
   * Whether to auto-initialize on mount.
   * Defaults to true.
   */
  autoInitialize?: boolean;
}

// ============================================================================
// Internal helpers
// ============================================================================

/** Enumerate audio input devices, deduplicate, and format. */
async function enumerateAudioDevices(): Promise<AudioDevice[]> {
  const allDevices = await navigator.mediaDevices.enumerateDevices();

  const audioInputs = allDevices.filter(
    (d) => d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'default',
  );
  // Exclude virtual/broken endpoints (e.g. Windows "Communications" devices)
  const withoutCommunications = audioInputs.filter(
    (d) => !(d.label ?? '').toLowerCase().includes(COMMUNICATIONS_LABEL_MARKER),
  );
  const deduped = deduplicateAudioDevices(withoutCommunications);
  return deduped.map((d) => ({
    deviceId: d.deviceId,
    label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`,
    kind: d.kind,
  }));
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for microphone permission and device management.
 *
 * Queries browser APIs directly -- no Jotai atoms for browser state.
 * Initializes on mount: checks permission, enumerates devices, sets up listeners.
 *
 * The only atom dependency is `hasAudioActivityAtom` (app-level state set by VoiceCard).
 */
export function useMicrophone(
  options: UseMicrophoneOptions = {},
): UseMicrophoneReturn {
  const { autoInitialize = true } = options;

  // ---- React state (replaces atoms) ----
  const [permissionState, setPermissionState] =
    useState<MicrophonePermissionState>('unknown');
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const hasAudioActivity = useAtomValue(hasAudioActivityAtom);
  const isRecordingActive = useAtomValue(isRecordingSessionActiveAtom);

  // ---- Refs for stable access inside callbacks / listeners ----
  const permissionStateRef = useRef(permissionState);
  permissionStateRef.current = permissionState;

  const selectedDeviceIdRef = useRef(selectedDeviceId);
  selectedDeviceIdRef.current = selectedDeviceId;

  const devicesRef = useRef(devices);
  devicesRef.current = devices;

  const isRecordingActiveRef = useRef(isRecordingActive);
  isRecordingActiveRef.current = isRecordingActive;

  // ---- Derived values (replaces derived atoms) ----
  const hasPermission = permissionState === 'granted';

  const selectedDevice = useMemo(() => {
    if (!selectedDeviceId) return null;
    return devices.find((d) => d.deviceId === selectedDeviceId) ?? null;
  }, [devices, selectedDeviceId]);

  const hasValidSelection = useMemo(() => {
    if (!selectedDeviceId) return false;
    return devices.some((d) => d.deviceId === selectedDeviceId);
  }, [devices, selectedDeviceId]);

  const canRecord = hasPermission && devices.length > 0 && hasAudioActivity;

  const audioConstraints = useMemo((): MediaTrackConstraints => {
    const extras: Omit<MediaTrackConstraints, 'deviceId'> = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    };
    const deviceId =
      selectedDeviceId && hasValidSelection ? selectedDeviceId : null;
    const result = buildAudioConstraints(deviceId, extras);
    // buildAudioConstraints returns `true` when no deviceId and no extras,
    // but here we always have extras so it always returns an object.
    return result as MediaTrackConstraints;
  }, [selectedDeviceId, hasValidSelection]);

  // ---- Core async operations ----

  /**
   * Enumerate devices and auto-select if the current selection is invalid.
   * Returns the approved device name (or null).
   */
  const refreshDevices = useCallback(async (): Promise<string | null> => {
    try {
      const audioDevices = await enumerateAudioDevices();
      setDevices(audioDevices);

      // Auto-select if current selection is invalid
      const currentId = selectedDeviceIdRef.current;
      const isCurrentValid =
        currentId && audioDevices.some((d) => d.deviceId === currentId);

      let approvedDeviceName: string | null = null;

      if (!isCurrentValid && audioDevices.length > 0) {
        const approvedDevice = findApprovedDevice(audioDevices);
        const deviceToSelect = approvedDevice ?? audioDevices[0];

        if (deviceToSelect) {
          debug.log('Auto-selecting device:', deviceToSelect.label);
          setSelectedDeviceId(deviceToSelect.deviceId);
        }

        approvedDeviceName = approvedDevice?.label ?? null;
      }

      return approvedDeviceName;
    } catch (err) {
      debug.error('Failed to enumerate devices:', err);
      setError(err as Error);

      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uses only refs and setState
  }, []);

  // Stable ref so event listeners always call the latest version
  const refreshDevicesRef = useLatest(refreshDevices);

  /**
   * Check permission state without prompting the user.
   * Uses Permissions API if available, otherwise tries device enumeration.
   */
  const checkPermission = useCallback(async () => {
    const previousState = permissionStateRef.current;

    try {
      // Try Permissions API first
      if (navigator.permissions?.query) {
        try {
          const result = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          });

          // If granted, verify we can actually access the mic
          if (result.state === 'granted') {
            try {
              const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
              });
              stream.getTracks().forEach((track) => track.stop());

              setPermissionState('granted');

              // If permission just changed to granted, clear errors and refresh devices
              if (previousState !== 'granted') {
                debug.log(
                  'Permission changed to granted, clearing errors and refreshing devices',
                );
                setError(null);
                await refreshDevicesRef.current();
              }
            } catch {
              // Permission says granted but access failed
              setPermissionState('denied');
              setSelectedDeviceId(null);
            }
          } else {
            const newState = result.state as MicrophonePermissionState;
            setPermissionState(newState);

            // If permission just changed to denied, clear device selection
            if (newState === 'denied' && previousState !== 'denied') {
              setSelectedDeviceId(null);
            }
          }
          return;
        } catch (e) {
          // Permissions API not supported for microphone query, fall through
          debug.log('Permissions API query not supported, using fallback:', e);
        }
      }

      // Fallback: check if device enumeration returns labels
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = allDevices.filter((d) => d.kind === 'audioinput');
      const hasLabels = audioDevices.some(
        (d) => d.label && d.label.trim() !== '',
      );

      if (hasLabels) {
        setPermissionState('granted');

        if (previousState !== 'granted') {
          debug.log(
            'Permission changed to granted (fallback), clearing errors and refreshing devices',
          );
          setError(null);
          await refreshDevicesRef.current();
        }
      } else if (audioDevices.length === 0) {
        setPermissionState('denied');
        setSelectedDeviceId(null);
      } else {
        setPermissionState('prompt');
      }
    } catch (err) {
      debug.error('Error checking permission:', err);
      setPermissionState('unknown');
      setError(err as Error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
  }, []);

  // Stable ref so event listeners always call the latest version
  const checkPermissionRef = useLatest(checkPermission);

  /**
   * Request microphone permission from the user.
   * Shows browser permission prompt with a 30-second timeout.
   */
  const requestPermission =
    useCallback(async (): Promise<MicrophonePermissionRequestResult> => {
      debug.log('Requesting microphone permission');
      setIsLoading(true);
      setError(null);

      // Check current permission state via Permissions API before requesting
      let permissionStateBefore: PermissionState | null = null;
      if (navigator.permissions?.query) {
        try {
          const status = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          });
          permissionStateBefore = status.state;
          debug.log('Permission state before request:', permissionStateBefore);
        } catch {
          // Permissions API not supported, continue without this check
        }
      }

      const requestStartTime = Date.now();

      try {
        const stream = await withTimeout(
          navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              channelCount: 1,
            },
          }),
          PERMISSION_REQUEST_TIMEOUT_MS,
        );

        // Success - clean up stream
        stream.getTracks().forEach((track) => track.stop());

        setPermissionState('granted');
        setError(null);

        // Enumerate devices now that we have permission
        await refreshDevicesRef.current();

        return { granted: true } as const;
      } catch (err) {
        const reqError = err as Error;
        const requestDuration = Date.now() - requestStartTime;

        // Detect if the browser instantly rejected without showing a prompt
        const wasInstantRejection =
          permissionStateBefore === 'prompt' &&
          (reqError.name === 'NotAllowedError' ||
            reqError.name === 'PermissionDeniedError') &&
          requestDuration < 500;

        if (wasInstantRejection) {
          debug.log(
            'Browser instantly rejected without prompting (duration:',
            requestDuration,
            'ms)',
          );
          const promptBlockedError =
            new MicrophonePermissionPromptBlockedError();
          setError(promptBlockedError);
          setPermissionState('prompt');
          setIsLoading(false);
          return { granted: false, reason: 'prompt-blocked' } as const;
        }

        setError(reqError);

        // Report timeout errors to Sentry for monitoring
        if (reqError instanceof MicrophonePermissionTimeoutError) {
          debug.error('Permission request timed out');
          import('@sentry/browser')
            .then((Sentry) => {
              Sentry.captureException(reqError, {
                tags: {
                  feature: 'microphone_permission',
                  error_type: 'timeout',
                },
                extra: {
                  timeout_ms: PERMISSION_REQUEST_TIMEOUT_MS,
                  user_agent: navigator.userAgent,
                },
              });
            })
            .catch(() => {
              debug.log('Sentry not available for error reporting');
            });
        }

        let reason: 'denied' | 'timeout' | 'error';
        if (
          reqError.name === 'NotAllowedError' ||
          reqError.name === 'PermissionDeniedError'
        ) {
          setPermissionState('denied');
          reason = 'denied';
        } else if (reqError.name === 'NotFoundError') {
          setPermissionState('denied');
          reason = 'error';
        } else if (reqError instanceof MicrophonePermissionTimeoutError) {
          reason = 'timeout';
          setPermissionState('unknown');
        } else {
          setPermissionState('unknown');
          reason = 'error';
        }

        setSelectedDeviceId(null);
        return { granted: false, reason } as const;
      } finally {
        setIsLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
    }, []);

  /**
   * Select a specific device by ID.
   */
  const selectDevice = useCallback((deviceId: string | null) => {
    if (deviceId === null) {
      setSelectedDeviceId(null);
      return;
    }

    const currentDevices = devicesRef.current;
    const device = currentDevices.find((d) => d.deviceId === deviceId);
    if (device) {
      debug.log('Selecting device:', device.label);
      setSelectedDeviceId(deviceId);
    } else {
      debug.warn('Device not found:', deviceId);
    }
  }, []);

  const getAudioConstraints = useCallback(
    () => audioConstraints,
    [audioConstraints],
  );

  // ---- Initialization & event listeners ----

  useEffect(() => {
    if (!autoInitialize) return;

    debug.log('Initializing microphone state');
    let isCleanedUp = false;

    // Initialize: check permission (which triggers device enumeration if granted)
    checkPermissionRef.current();

    // ---- Event Listeners ----

    const handleDeviceChange = () => {
      debug.log('Device change detected');
      refreshDevicesRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      // Skip re-verification while actively recording — we already have a
      // working stream, and a getUserMedia call could cause audio glitches.
      if (isRecordingActiveRef.current) {
        debug.log(
          'Window became visible but recording is active, skipping permission re-check',
        );
        return;
      }

      debug.log('Window became visible, re-checking permission');
      checkPermissionRef.current();
    };

    // Set up listeners synchronously
    navigator.mediaDevices?.addEventListener(
      'devicechange',
      handleDeviceChange,
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Try to set up Permissions API listener (async but we don't wait for it)
    let permissionStatus: PermissionStatus | null = null;
    const handlePermissionChange = () => {
      debug.log('Permission change detected via Permissions API');
      checkPermissionRef.current();
    };

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'microphone' as PermissionName })
        .then((status) => {
          if (isCleanedUp) {
            debug.log(
              'Skipping Permissions API listener setup - already cleaned up',
            );
            return;
          }
          permissionStatus = status;
          permissionStatus.addEventListener('change', handlePermissionChange);
        })
        .catch((e) => {
          debug.log('Permissions API change listener not supported:', e);
        });
    }

    // Cleanup
    return () => {
      debug.log('Cleaning up microphone event listeners');
      isCleanedUp = true;
      navigator.mediaDevices?.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      permissionStatus?.removeEventListener('change', handlePermissionChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest refs are stable; run once on mount
  }, [autoInitialize]);

  return {
    // Permission state
    permissionState,
    hasPermission,
    canRecord,

    // Devices
    devices,
    selectedDeviceId,
    selectedDevice,
    hasValidSelection,

    // Actions
    requestPermission,
    refreshDevices,
    selectDevice,
    getAudioConstraints,

    // Status
    isLoading,
    error,
  };
}
